"use client";

import { getRouteStyles } from "@/lib/route-styles";
import { usePathname } from "next/navigation";
import { Suspense, ViewTransition, useEffect, useRef, type ReactNode } from "react";

import { CcBookCallPopupProvider } from "@/components/booking/CcBookCallPopupProvider";
import { CcNavigationProgressHandoff } from "@/components/ui/CcNavigationProgress";
import { CcHistoryPageTransition } from "./CcHistoryPageTransition";
import { CcFooter } from "@/components/layout/CcFooter";
import { CcSiteFooterClosingBandProvider } from "@/components/layout/CcSiteFooterClosingBand";
import { CcNavbar } from "@/components/layout/CcNavbar";
import { navCtaLabel, navHasDarkHero } from "@/lib/cc-nav-route";
import {
  isOverviewAttachedFooter,
  resolveSiteFooterChrome,
  type CcFooterLayout,
} from "@/lib/cc-footer-layout";
import type { NavResourceItem } from "@/lib/cc-nav-resources";
import type { NavResourceMegaColumn } from "@/lib/cc-nav-resources-mega";
import type { NavProjectMegaColumn } from "@/lib/cc-nav-projects-mega";
import type { NavProjectItem } from "@/lib/projects";
import type { NavServiceMegaColumn } from "@/lib/cc-nav-services-mega";
import type { NavServiceItem } from "@/lib/services";
import {
  getSiteFooterIndustriesMegaColumn,
  getSiteFooterServiceMegaColumns,
  getSiteNavCtaHref,
} from "@/lib/cc-site-footer-nav";
import { buildLocalizedPath, toLogicalPathname } from "@/lib/i18n/paths";
import { toSiteLocale } from "@/lib/i18n/config";
import type { NavBreadcrumbTrail } from "@/lib/cc-nav-breadcrumb";
import type { BookCallPopupVariant } from "@/lib/book-call-popup-copy";
import { useSiteChromeLocked } from "@/components/layout/CcSiteChromeLock";
import type { Lang } from "@/lib/types";
import { isCampaignLandingPathname } from "@/lib/campaign-landing-pages";

type CcSiteChromeProps = {
  children: ReactNode;
  lang: Lang;
  links: { label: string; href: string }[];
  serviceItems: NavServiceItem[];
  serviceMegaColumns: NavServiceMegaColumn[];
  projectItems: NavProjectItem[];
  projectMegaColumns: NavProjectMegaColumn[];
  resourceItems: NavResourceItem[];
  resourceMegaColumns: NavResourceMegaColumn[];
  serviceLinks: { label: string; href: string }[];
  homeCtaLabel: string;
  bookCallCtaLabel: string;
  /** Sticky nav label on `/seo` — opens book-call popup with SEO copy. */
  seoBookCallCtaLabel: string;
  /** Homepage only: true when {@link productionHomeUsesDarkNavBar} — dark nav pill over the hero. */
  homeHasDarkHero: boolean;
  /** Pathname from the server request — stable for SSR when `usePathname()` is unset. */
  initialPathname: string;
  /** Breadcrumb for `initialPathname` (blog post titles from static store on the server). */
  initialNavBreadcrumb?: NavBreadcrumbTrail | null;
  /** When set, overrides pathname-based {@link footerLayout}. */
  footerLayoutOverride?: CcFooterLayout;
  /** When set, overrides pathname-based {@link footerContinuesClosingBand}. */
  footerContinuesClosingOverride?: boolean;
  /** When set, overrides pathname-based {@link footerUsesAntiquePhoto}. */
  footerAntiquePhotoOverride?: boolean;
};

function siteChromeClassName(layout: CcFooterLayout): string {
  const classes = ["cc-site-chrome"];

  if (isOverviewAttachedFooter(layout)) {
    classes.push("cc-site-chrome--footer-attached");
  }

  return classes.join(" ");
}

export function CcSiteChrome(props: CcSiteChromeProps) {
  return <><Suspense fallback={null}><CcNavigationProgressHandoff /></Suspense><CcSiteChromeContent {...props} /></>;
}

function CcSiteChromeContent({
  children,
  lang,
  links,
  serviceItems,
  serviceMegaColumns,
  projectItems,
  projectMegaColumns,
  resourceItems,
  resourceMegaColumns,
  serviceLinks,
  homeCtaLabel,
  bookCallCtaLabel,
  seoBookCallCtaLabel,
  homeHasDarkHero,
  initialPathname,
  initialNavBreadcrumb = null,
  footerLayoutOverride,
  footerContinuesClosingOverride,
  footerAntiquePhotoOverride,
}: CcSiteChromeProps) {
  const chromeLocked = useSiteChromeLocked();
  const clientPathname = usePathname();
  const pathname = clientPathname ?? initialPathname;
  const firstPath = useRef(pathname);
  const navigated = useRef(false);
  const animateNavigation = navigated.current || pathname !== firstPath.current;
  useEffect(() => {
    if (pathname !== firstPath.current) navigated.current = true;
  }, [pathname]);
  const footerChrome = resolveSiteFooterChrome(pathname);
  const resolvedFooterLayout =
    footerLayoutOverride ?? footerChrome.layout;

  if (pathname === "/dev" || pathname.startsWith("/dev/")) {
    return <>{children}</>;
  }

  // Ad landing pages bring their own minimal top bar and no footer: booking is the only exit.
  if (isCampaignLandingPathname(pathname)) {
    return (
      <CcBookCallPopupProvider lang={lang}>
        {getRouteStyles(pathname).map(style => <link key={style.href} rel="stylesheet" {...style} />)}
        <div className="cc-site-chrome cc-site-chrome--campaign">{children}</div>
      </CcBookCallPopupProvider>
    );
  }

  const hasDarkHero = navHasDarkHero(pathname, homeHasDarkHero);
  const locale = toSiteLocale(lang);
  const logicalPathname = toLogicalPathname(pathname);
  const isContactPage = logicalPathname === "/contact";
  const navBookCallVariant: BookCallPopupVariant | undefined =
    logicalPathname === "/seo"
      ? "seo"
      : logicalPathname === "/ai-automation/hubspot"
        ? "hubspot"
        : logicalPathname === "/website-design/branding" ||
            logicalPathname === "/website-design/varumarke"
          ? "branding"
          : undefined;
  const privacyHref = buildLocalizedPath({ locale, customPath: "privacy-policy" });
  const footerServiceMegaColumns = getSiteFooterServiceMegaColumns(lang);
  const industriesMegaColumn = getSiteFooterIndustriesMegaColumn(lang);

  return (
    <CcBookCallPopupProvider lang={lang}>
      {getRouteStyles(pathname).map(style => <link key={style.href} rel="stylesheet" {...style} />)}
      <CcSiteFooterClosingBandProvider>
        <div
          className={`${siteChromeClassName(resolvedFooterLayout)}${chromeLocked ? " cc-site-chrome--locked" : ""}`}
        >
          {!chromeLocked ? (
            <ViewTransition default="none" update={animateNavigation ? "cc-page-navigation" : "none"}>
              <div className="cc-page-navigation-anchor">
                <CcNavbar
                  lang={lang}
                  links={links}
                  serviceItems={serviceItems}
                  serviceMegaColumns={serviceMegaColumns}
                  projectItems={projectItems}
                  projectMegaColumns={projectMegaColumns}
                  resourceItems={resourceItems}
                  resourceMegaColumns={resourceMegaColumns}
                  ctaLabel={navCtaLabel(
                    pathname,
                    homeCtaLabel,
                    bookCallCtaLabel,
                    seoBookCallCtaLabel,
                  )}
                  ctaHref={getSiteNavCtaHref(lang)}
                  ctaIcon={isContactPage ? "calendar" : "arrow"}
                  bookCallVariant={navBookCallVariant}
                  hasDarkHero={hasDarkHero}
                  initialPathname={initialPathname}
                  initialNavBreadcrumb={initialNavBreadcrumb}
                />
              </div>
            </ViewTransition>
          ) : null}
          {/* Route keys animate page replacement without remounting the navbar. */}
          <CcHistoryPageTransition pathname={pathname}>
            <ViewTransition
              key={pathname}
              default="none"
              enter={animateNavigation ? "cc-page-enter" : "none"}
              exit={animateNavigation ? "cc-page-exit" : "none"}
            >
              {children}
              {!chromeLocked ? (
                <CcFooter
                  links={links}
                  serviceLinks={serviceLinks}
                  serviceMegaColumns={footerServiceMegaColumns}
                  industriesMegaColumn={industriesMegaColumn}
                  privacyHref={privacyHref}
                  navTheme="dark"
                  continuesClosingBand={
                    footerContinuesClosingOverride ?? footerChrome.continuesClosingBand
                  }
                  useAntiquePhoto={
                    footerAntiquePhotoOverride ?? footerChrome.useAntiquePhoto
                  }
                  layout={resolvedFooterLayout}
                />
              ) : null}
            </ViewTransition>
          </CcHistoryPageTransition>
        </div>
      </CcSiteFooterClosingBandProvider>
    </CcBookCallPopupProvider>
  );
}
